// Independent of A-F CCMR — do not import Ccmr/* or write ccmrIndicators*.
import firebase from 'firebase';
import { determineFileType } from '../../Assessments/+store/assessments/assessments.validation2';
import { getDynamicAssessmentModel, AssessmentTypes } from '../../Assessments/+store/assessments/assessments.types';
import { createAssessmentSet, saveAssessmentSet } from '../../Assessments/+store/assessments/assessments.db';
import { saveAssessment } from '../../Assessments/+store/assessments/assessments.storage';
import { onFileUpload } from '../../Assessments/+store/assessments/assessments.api';

// HB3 CCMR Outcomes Bonus read endpoint (auth-gated onCall). Returns the district
// funding aggregate for the requested grad year, a year-over-year trend (aggregate
// only), and a role-scoped near-qualifier worklist.
const getEndpoint = firebase.functions().httpsCallable('getOutcomesBonus');

export const getOutcomesBonus = async (gradYear) => {
  const result = await getEndpoint({ gradYear });
  return result && result.data;
};

// Upload a TEA CCMR Outcomes Bonus xlsx straight from the OB page, reusing the exact
// Assessment Manager pipeline (create set -> save file to storage -> save set record ->
// trigger backend processing). We first confirm the dropped file is actually an OB
// report so the focused uploader can give a clear error instead of silently ingesting a
// CCMR Verifier / roster / other file dropped here. After the backend writes the node we
// read it back so the upload epic can fire a funding-aware toast without a second round
// trip. This pipeline never touches the A-F CCMR ingest.
export const uploadOutcomesBonusFile = async (profile, file, selectedGradYear) => {
  const fileType = await determineFileType(file);
  const detectedType = fileType && fileType.type;
  if (!detectedType || String(detectedType).indexOf(AssessmentTypes.CCMROutcomesBonus) !== 0) {
    const err = new Error(
      'That file was not recognized as a CCMR Outcomes Bonus file. Upload the "CCMR_Outcomes_Bonus_<district#>.xlsx" export from TEAL.'
    );
    err.code = 'notCcmrOutcomesBonus';
    throw err;
  }
  // The TEA OB file is named by district, not graduating class, so the user's picked year is
  // authoritative. Fall back to the year detected from the file name only if none was chosen.
  const gradYear = selectedGradYear || String(detectedType).slice(AssessmentTypes.CCMROutcomesBonus.length);
  const type = AssessmentTypes.CCMROutcomesBonus + gradYear;
  const fields = { name: getDynamicAssessmentModel(type).name };
  const assessmentSetObj = await createAssessmentSet(profile, file, type, fields);
  await saveAssessment(profile, assessmentSetObj, file, () => {});
  await saveAssessmentSet(assessmentSetObj, profile);
  const [[assessmentSetId, assessmentSet]] = Object.entries(assessmentSetObj);
  // Backend onFileUpload parses the file, aggregates the funding math, and writes the
  // outcomesBonus/<gradYear> node. Awaiting it means the node is ready before we refetch.
  await onFileUpload({ name: assessmentSet.file.storageKey, assessmentSetId });
  // Read back the freshly-written aggregate so the toast can show real dollars.
  const node = await getOutcomesBonus(gradYear);
  const aggregate = (node && node.aggregate) || {};
  return {
    assessmentSetId,
    gradYear,
    fileName: file.name,
    totalDollars: aggregate.totalDollars || 0,
    countsType: aggregate.countsType || 'final',
  };
};

export default 'outcomesBonus.api.js';
